import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { CellContext } from '@tanstack/react-table';
import { Button } from '@ui/button.tsx';
import { Trash2Icon, PencilIcon } from 'lucide-react';
import type { GameGameCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { UserFullname } from '@r/components/user-fullname.tsx';

type Game = GameGameCollectionReadModel & {
  createdBy?: { user: { fullname: string; isDeleted?: boolean } };
};

export const useGameColumns = (
  onDelete: (game: Game) => void,
  onEdit: (game: Game) => void
): ColumnDef<Game>[] => {
  const { t } = useTranslation();

  return [
    {
      accessorKey: 'label',
      header: t('games.list.column.name')
    },
    {
      accessorKey: 'openingDate',
      header: t('games.list.column.start'),
      cell: ({ row }) => {
        if (row.original.openingDate) {
          return format(new Date(row.original.openingDate), 'dd/MM/yyyy', { locale: fr });
        }
        return null;
      }
    },
    {
      accessorKey: 'closingDate',
      header: t('games.list.column.end'),
      cell: ({ row }) => {
        if (row.original.closingDate) {
          return format(new Date(row.original.closingDate), 'dd/MM/yyyy', { locale: fr });
        }
        return null;
      }
    },
    {
      accessorKey: 'createdBy',
      header: t('games.list.column.user'),
      cell: ({ row }: CellContext<Game, unknown>) => {
        return (
          <UserFullname
            fullname={row.original.createdBy?.user.fullname ?? ''}
            isDeleted={row.original.createdBy?.user.isDeleted}
          />
        );
      }
    },
    {
      accessorKey: 'status',
      header: t('games.list.column.status'),
      cell: ({ row }: CellContext<Game, unknown>) => {
        const status = row.original.status;
        if (status === 'game.status.planned') {
          return t('games.status.planned');
        }
        if (status === 'game.status.active') {
          return t('games.status.active');
        }
        if (status === 'game.status.finished') {
          return t('games.status.finished');
        }
        return status;
      }
    },
    {
      accessorKey: 'segmentsCount',
      header: t('games.list.column.segments'),
      cell: ({ row }: CellContext<Game, unknown>) => {
        const segmentsCount = row.original.segmentsCount ?? 0;
        // Sans segment le jeu est visible par tous : on ne met en avant que les jeux conditionnés
        return segmentsCount > 0 ? (
          <span className="font-semibold text-primary">{segmentsCount}</span>
        ) : (
          <span className="text-muted-foreground">{segmentsCount}</span>
        );
      }
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const isFinished = row.original.status === 'game.status.finished';
        return (
          <div className="flex gap-4 justify-end items-center">
            {!isFinished && (
              <>
                <Button
                  variant={'raw'}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground h-fit"
                  onClick={() => onEdit(row.original)}
                  title={t('common.edit')}
                >
                  <PencilIcon size={18} />
                </Button>
                <span className="text-muted-foreground/40">|</span>
              </>
            )}
            <Button
              variant={'raw'}
              className="p-1.5 rounded-md text-destructive/60 hover:text-destructive h-fit"
              onClick={() => onDelete(row.original)}
              title={t('common.delete')}
            >
              <Trash2Icon size={18} />
            </Button>
          </div>
        );
      }
    }
  ];
};
